import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Typography,
  message,
  Drawer,
  Descriptions,
  Divider,
  Popconfirm,
  Progress,
  Tooltip,
  Spin,
  Empty,
  Image,
  Checkbox,
  Pagination,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EnvironmentOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import PhotoLocationMap from '../components/PhotoLocationMap';
import SegmentAudioPlayer from '../components/SegmentAudioPlayer';
import { API_CONFIG } from '../config/api';
import { secureLogger } from '../utils/secureLogger';
import { ensureFaceMediaCookie } from '../utils/faceMediaCookie';
import {
  EnrollmentJob,
  EnrollmentSegment,
  EnrollmentSummary,
  FaceClusterAssignResponse,
  FaceClusterFace,
  FaceClusterFacesResponse,
  FaceClusterListResponse,
  FaceClusterReclusterCandidate,
  FaceClusterReclusterResponse,
  FaceClusterSummary,
  FacePhotoCandidate,
  FacePhotoCandidateResponse,
  PersonDetail,
  PersonSummary,
  PersonListResponse,
} from '../types/api';

const { Paragraph, Text } = Typography;
const { TextArea } = Input;

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const stringifyMetadata = (metadata?: Record<string, unknown>) => {
  if (!metadata || Object.keys(metadata).length === 0) {
    return '';
  }
  try {
    return JSON.stringify(metadata, null, 2);
  } catch {
    return '';
  }
};

const FACE_LIST_PAGE_LIMIT = 200;
const UNASSIGNED_CLUSTER_PAGE_SIZE = 100;

type PersonsWebSocketMessage = {
  type?: string;
  timestamp?: string;
  event_id?: string;
  data?: {
    requested_cluster_id?: string;
    primary_cluster_id?: string;
    primary_cluster_size?: number;
    merged_cluster_ids?: string[];
    merged_count?: number;
    iterations?: number;
    mode?: string;
    error?: string;
  };
};

type PersonsPageProps = {
  onWebSocketMessage?: (handler: ((message: PersonsWebSocketMessage) => void) | null) => void;
};

const PersonsPage: React.FC<PersonsPageProps> = ({ onWebSocketMessage }) => {
  const [persons, setPersons] = useState<PersonSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [includeShared, setIncludeShared] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPerson, setEditingPerson] = useState<PersonSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPerson, setDetailPerson] = useState<PersonSummary | null>(null);
  const [detailData, setDetailData] = useState<PersonDetail | null>(null);
  const [unlinkedFaces, setUnlinkedFaces] = useState<FacePhotoCandidate[]>([]);
  const [unlinkedTotal, setUnlinkedTotal] = useState(0);
  const [unlinkedLoading, setUnlinkedLoading] = useState(false);
  const [pendingFacePhotoId, setPendingFacePhotoId] = useState<string | null>(null);
  const [pendingClusterId, setPendingClusterId] = useState<string | null>(null);
  const [assignedFaceClusters, setAssignedFaceClusters] = useState<FaceClusterSummary[]>([]);
  const [assignedFaceClustersTotal, setAssignedFaceClustersTotal] = useState(0);
  const [unassignedFaceClusters, setUnassignedFaceClusters] = useState<FaceClusterSummary[]>([]);
  const [unassignedFaceClustersTotal, setUnassignedFaceClustersTotal] = useState(0);
  const [faceClustersLoading, setFaceClustersLoading] = useState(false);
  const [unassignedClustersRefreshing, setUnassignedClustersRefreshing] = useState(false);
  const [clusterDrawerVisible, setClusterDrawerVisible] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState<FaceClusterSummary | null>(null);
  const [clusterFaces, setClusterFaces] = useState<FaceClusterFace[]>([]);
  const [clusterFacesLoading, setClusterFacesLoading] = useState(false);
  const [clusterAssignVisible, setClusterAssignVisible] = useState(false);
  const [clusterAssignTarget, setClusterAssignTarget] = useState<FaceClusterSummary | null>(null);
  const [clusterAssignPersonId, setClusterAssignPersonId] = useState<string | null>(null);
  const [clusterAssigning, setClusterAssigning] = useState(false);
  const [clusterRecalculatingId, setClusterRecalculatingId] = useState<string | null>(null);
  const [reclusterModalVisible, setReclusterModalVisible] = useState(false);
  const [reclusterModalTarget, setReclusterModalTarget] = useState<FaceClusterSummary | null>(null);
  const [reclusterModalResult, setReclusterModalResult] = useState<FaceClusterReclusterResponse | null>(null);
  const [reclusterCandidateSelection, setReclusterCandidateSelection] = useState<string[]>([]);
  const [reclusterCandidateMerging, setReclusterCandidateMerging] = useState(false);
  const [clusterRepresentativeUpdatingFaceId, setClusterRepresentativeUpdatingFaceId] = useState<string | null>(null);
  const [personRepresentativeUpdatingFaceId, setPersonRepresentativeUpdatingFaceId] = useState<string | null>(null);
  const [photoDetailDrawerVisible, setPhotoDetailDrawerVisible] = useState(false);
  const [selectedClusterFace, setSelectedClusterFace] = useState<FaceClusterFace | null>(null);
  const [bulkClusterAssignPersonId, setBulkClusterAssignPersonId] = useState<string | null>(null);
  const [selectedClusterIds, setSelectedClusterIds] = useState<string[]>([]);
  const [bulkClusterAssigning, setBulkClusterAssigning] = useState(false);
  const [unassignedClusterPage, setUnassignedClusterPage] = useState(1);
  const [form] = Form.useForm();
  const canEditDetail = detailPerson
    ? !currentUserId || detailPerson.owner_user_id === currentUserId
    : false;

  const getAuthToken = useCallback(async () => {
    try {
      const { fetchAuthSession } = await import('../auth/gateway');
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) {
        throw new Error('認証情報が取得できませんでした');
      }
      return token;
    } catch (err) {
      secureLogger.error('Failed to resolve auth token', { err });
      throw new Error('認証に失敗しました');
    }
  }, []);

  const resolveCurrentUserId = useCallback(async () => {
    if (currentUserId) {
      return currentUserId;
    }
    try {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS}:resolve`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (response.ok) {
        const data = await response.json();
        setCurrentUserId(data.id);
        return data.id as string;
      }
    } catch (err) {
      secureLogger.warn('Failed to resolve backend user id', { err });
    }
    return null;
  }, [currentUserId, getAuthToken]);

  const fetchPersons = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      await ensureFaceMediaCookie(token);
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERSONS}?include_shared=${includeShared ? 'true' : 'false'}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) {
        throw new Error('人物一覧の取得に失敗しました');
      }
      const data: PersonListResponse = await response.json();
      setPersons(data.persons || []);
    } catch (err) {
      const messageText = err instanceof Error ? err.message : '人物一覧の取得に失敗しました';
      setError(messageText);
      message.error(messageText);
    } finally {
      setLoading(false);
    }
  }, [getAuthToken, includeShared]);

  const fetchUnlinkedFaces = useCallback(async () => {
    setUnlinkedLoading(true);
    try {
      const token = await getAuthToken();
      await ensureFaceMediaCookie(token);
      const faces: FacePhotoCandidate[] = [];
      let total = 0;
      let offset = 0;
      while (true) {
        const response = await fetch(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERSONS}/face-photos/unlinked?limit=${FACE_LIST_PAGE_LIMIT}&offset=${offset}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (!response.ok) {
          throw new Error('未登録の顔写真の取得に失敗しました');
        }
        const data: FacePhotoCandidateResponse = await response.json();
        if (offset === 0) {
          total = data.total || 0;
        }
        const pageFaces = data.faces || [];
        faces.push(...pageFaces);
        if (pageFaces.length === 0 || faces.length >= total) {
          break;
        }
        offset += pageFaces.length;
      }
      setUnlinkedFaces(faces);
      setUnlinkedTotal(total);
    } catch (err) {
      const messageText =
        err instanceof Error ? err.message : '未登録の顔写真の取得に失敗しました';
      message.error(messageText);
    } finally {
      setUnlinkedLoading(false);
    }
  }, [getAuthToken]);

  const fetchAssignedClustersData = useCallback(async (token: string) => {
    const assignedClusters: FaceClusterSummary[] = [];
    let assignedTotal = 0;
    let assignedOffset = 0;
    while (true) {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERSONS}/face-clusters?linked=assigned&limit=${FACE_LIST_PAGE_LIMIT}&offset=${assignedOffset}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) {
        throw new Error('顔クラスタの取得に失敗しました');
      }
      const data: FaceClusterListResponse = await response.json();
      if (assignedOffset === 0) {
        assignedTotal = data.total || 0;
      }
      const pageClusters = data.clusters || [];
      assignedClusters.push(...pageClusters);
      if (pageClusters.length === 0 || assignedClusters.length >= assignedTotal) {
        break;
      }
      assignedOffset += pageClusters.length;
    }
    return { clusters: assignedClusters, total: assignedTotal };
  }, []);

  const fetchUnassignedClustersPageData = useCallback(async (token: string, page: number) => {
    const fetchPage = async (targetPage: number): Promise<FaceClusterListResponse> => {
      const offset = (targetPage - 1) * UNASSIGNED_CLUSTER_PAGE_SIZE;
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERSONS}/face-clusters?linked=unassigned&limit=${UNASSIGNED_CLUSTER_PAGE_SIZE}&offset=${offset}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) {
        throw new Error('未紐づけ顔クラスタの取得に失敗しました');
      }
      return response.json();
    };

    const requestedPage = Math.max(1, page);
    let unassignedData = await fetchPage(requestedPage);
    const unassignedTotal = unassignedData.total || 0;
    const maxPage = Math.max(1, Math.ceil(unassignedTotal / UNASSIGNED_CLUSTER_PAGE_SIZE));
    const resolvedPage = Math.min(requestedPage, maxPage);

    if (resolvedPage !== requestedPage) {
      unassignedData = await fetchPage(resolvedPage);
    }

    return {
      clusters: unassignedData.clusters || [],
      total: unassignedTotal,
      resolvedPage,
    };
  }, []);

  const fetchFaceClusters = useCallback(async (targetUnassignedPage: number = 1) => {
    setFaceClustersLoading(true);
    try {
      const token = await getAuthToken();
      await ensureFaceMediaCookie(token);
      const [assignedData, unassignedData] = await Promise.all([
        fetchAssignedClustersData(token),
        fetchUnassignedClustersPageData(token, targetUnassignedPage),
      ]);

      setAssignedFaceClusters(assignedData.clusters);
      setAssignedFaceClustersTotal(assignedData.total);
      setUnassignedFaceClusters(unassignedData.clusters);
      setUnassignedFaceClustersTotal(unassignedData.total);
      setUnassignedClusterPage(unassignedData.resolvedPage);

      const visibleClusters = [...assignedData.clusters, ...unassignedData.clusters];
      setSelectedClusterIds((prev) =>
        prev.filter((clusterId) =>
          visibleClusters.some((cluster) => cluster.cluster_id === clusterId),
        ),
      );
    } catch (err) {
      const messageText = err instanceof Error ? err.message : '顔クラスタの取得に失敗しました';
      message.error(messageText);
    } finally {
      setFaceClustersLoading(false);
    }
  }, [fetchAssignedClustersData, fetchUnassignedClustersPageData, getAuthToken]);

  const refreshFaceClusters = useCallback(async () => {
    await fetchFaceClusters(unassignedClusterPage);
  }, [fetchFaceClusters, unassignedClusterPage]);

  const refreshUnassignedClusters = useCallback(async (targetPage: number = unassignedClusterPage) => {
    setUnassignedClustersRefreshing(true);
    try {
      const token = await getAuthToken();
      const unassignedData = await fetchUnassignedClustersPageData(token, targetPage);

      setUnassignedFaceClusters(unassignedData.clusters);
      setUnassignedFaceClustersTotal(unassignedData.total);
      setUnassignedClusterPage(unassignedData.resolvedPage);

      const visibleClusters = [...assignedFaceClusters, ...unassignedData.clusters];
      setSelectedClusterIds((prev) =>
        prev.filter((clusterId) =>
          visibleClusters.some((cluster) => cluster.cluster_id === clusterId),
        ),
      );
    } catch (err) {
      const messageText = err instanceof Error ? err.message : '未紐づけ顔クラスタの取得に失敗しました';
      message.error(messageText);
    } finally {
      setUnassignedClustersRefreshing(false);
    }
  }, [
    assignedFaceClusters,
    fetchUnassignedClustersPageData,
    getAuthToken,
    unassignedClusterPage,
  ]);

  const syncAssignedClustersInBackground = useCallback(async () => {
    try {
      const token = await getAuthToken();
      const assignedData = await fetchAssignedClustersData(token);
      setAssignedFaceClusters(assignedData.clusters);
      setAssignedFaceClustersTotal(assignedData.total);
      const visibleClusters = [...assignedData.clusters, ...unassignedFaceClusters];
      setSelectedClusterIds((prev) =>
        prev.filter((clusterId) =>
          visibleClusters.some((cluster) => cluster.cluster_id === clusterId),
        ),
      );
    } catch (err) {
      secureLogger.warn('Failed to sync assigned clusters in background', { err });
    }
  }, [fetchAssignedClustersData, getAuthToken, unassignedFaceClusters]);

  const handlePersonsWebSocketMessage = useCallback(
    (wsMessage: PersonsWebSocketMessage) => {
      if (!wsMessage || typeof wsMessage !== 'object') {
        return;
      }

      if (wsMessage.type === 'persons.face_cluster.recluster.completed') {
        const payload = wsMessage.data || {};
        const mergedClusterIds = Array.isArray(payload.merged_cluster_ids)
          ? payload.merged_cluster_ids.filter(
              (clusterId): clusterId is string => typeof clusterId === 'string' && clusterId.length > 0,
            )
          : [];
        const mergedCount =
          typeof payload.merged_count === 'number' && Number.isFinite(payload.merged_count)
            ? Math.max(0, Math.trunc(payload.merged_count))
            : mergedClusterIds.length;
        const iterations =
          typeof payload.iterations === 'number' && Number.isFinite(payload.iterations)
            ? Math.max(0, Math.trunc(payload.iterations))
            : 0;
        const primaryClusterId =
          typeof payload.primary_cluster_id === 'string' && payload.primary_cluster_id.length > 0
            ? payload.primary_cluster_id
            : null;
        const primaryClusterSize =
          typeof payload.primary_cluster_size === 'number' && Number.isFinite(payload.primary_cluster_size)
            ? Math.max(0, Math.trunc(payload.primary_cluster_size))
            : null;
        const removedClusterIds = new Set<string>(mergedClusterIds);

        if (removedClusterIds.size > 0) {
          setUnassignedFaceClusters((prev) =>
            prev.filter((cluster) => !removedClusterIds.has(cluster.cluster_id)),
          );
          setSelectedClusterIds((prev) =>
            prev.filter((clusterId) => !removedClusterIds.has(clusterId)),
          );
        }

        if (mergedCount > 0) {
          setUnassignedFaceClustersTotal((prev) => Math.max(0, prev - mergedCount));
        }

        if (primaryClusterId) {
          setAssignedFaceClusters((prev) =>
            prev.map((cluster) =>
              cluster.cluster_id === primaryClusterId
                ? {
                    ...cluster,
                    size: primaryClusterSize ?? cluster.size,
                    updated_at: wsMessage.timestamp || cluster.updated_at,
                  }
                : cluster,
            ),
          );
          setSelectedCluster((prev) =>
            prev && prev.cluster_id === primaryClusterId
              ? {
                  ...prev,
                  size: primaryClusterSize ?? prev.size,
                  updated_at: wsMessage.timestamp || prev.updated_at,
                }
              : prev,
          );
        }

        if (mergedCount > 0) {
          message.info(`自動再計算が完了しました（${mergedCount}件吸収 / ${iterations}反復）`);
        } else {
          message.info(`自動再計算が完了しました（吸収なし / ${iterations}反復）`);
        }
        void syncAssignedClustersInBackground();
        return;
      }

      if (wsMessage.type === 'persons.face_cluster.recluster.failed') {
        message.warning('自動再計算に失敗しました。必要に応じて再計算を実行してください。');
      }
    },
    [syncAssignedClustersInBackground],
  );

  const fetchClusterFaces = useCallback(
    async (clusterId: string) => {
      setClusterFacesLoading(true);
      try {
        const token = await getAuthToken();
        await ensureFaceMediaCookie(token);
        const response = await fetch(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERSONS}/face-clusters/${clusterId}/faces`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (!response.ok) {
          throw new Error('クラスタ内の顔一覧取得に失敗しました');
        }
        const data: FaceClusterFacesResponse = await response.json();
        setClusterFaces(data.faces || []);
      } catch (err) {
        const messageText =
          err instanceof Error ? err.message : 'クラスタ内の顔一覧取得に失敗しました';
        message.error(messageText);
      } finally {
        setClusterFacesLoading(false);
      }
    },
    [getAuthToken],
  );

  useEffect(() => {
    resolveCurrentUserId();
  }, [resolveCurrentUserId]);

  useEffect(() => {
    fetchPersons();
  }, [fetchPersons]);

  useEffect(() => {
    fetchUnlinkedFaces();
  }, [fetchUnlinkedFaces]);

  useEffect(() => {
    fetchFaceClusters(1);
  }, [fetchFaceClusters]);

  useEffect(() => {
    if (!onWebSocketMessage) {
      return;
    }
    onWebSocketMessage(handlePersonsWebSocketMessage);
    return () => {
      onWebSocketMessage(null);
    };
  }, [handlePersonsWebSocketMessage, onWebSocketMessage]);

  const fetchPersonDetail = useCallback(
    async (personId: string) => {
      setDetailLoading(true);
      try {
        const token = await getAuthToken();
        await ensureFaceMediaCookie(token);
        const response = await fetch(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERSONS}/${personId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (!response.ok) {
          throw new Error('人物詳細の取得に失敗しました');
        }
        const data: PersonDetail = await response.json();
        setDetailData(data);
      } catch (err) {
        const messageText = err instanceof Error ? err.message : '人物詳細の取得に失敗しました';
        message.error(messageText);
        setDetailData(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [getAuthToken],
  );

  const openDetailDrawer = useCallback(
    async (person: PersonSummary) => {
      setDetailPerson(person);
      setDetailVisible(true);
      await fetchPersonDetail(person.id);
    },
    [fetchPersonDetail],
  );

  const closeDetailDrawer = useCallback(() => {
    setDetailVisible(false);
    setDetailPerson(null);
    setDetailData(null);
  }, []);

  const refreshDetail = useCallback(async () => {
    if (detailPerson) {
      await fetchPersonDetail(detailPerson.id);
    }
  }, [detailPerson, fetchPersonDetail]);

  const openCreateModal = useCallback(() => {
    setEditingPerson(null);
    setPendingFacePhotoId(null);
    setPendingClusterId(null);
    form.resetFields();
    setModalVisible(true);
  }, [form]);

  const openCreateModalFromFace = useCallback(
    (facePhotoId: string) => {
      setEditingPerson(null);
      setPendingFacePhotoId(facePhotoId);
      setPendingClusterId(null);
      form.resetFields();
      setModalVisible(true);
    },
    [form],
  );

  const openCreateModalFromCluster = useCallback(
    (clusterId: string) => {
      setEditingPerson(null);
      setPendingFacePhotoId(null);
      setPendingClusterId(clusterId);
      form.resetFields();
      setModalVisible(true);
    },
    [form],
  );

  const openEditModal = useCallback((person: PersonSummary) => {
    setEditingPerson(person);
    setPendingFacePhotoId(null);
    setPendingClusterId(null);
    form.setFieldsValue({
      display_name: person.display_name,
      phone_number: person.phone_number || '',
      email: person.email || '',
      affiliation: person.affiliation || '',
      notes: person.notes || '',
      metadataText: stringifyMetadata(person.metadata),
    });
    setModalVisible(true);
  }, [form]);

  const handleModalClose = useCallback(() => {
    setModalVisible(false);
    setEditingPerson(null);
    setPendingFacePhotoId(null);
    setPendingClusterId(null);
    form.resetFields();
  }, [form]);

  const openClusterDrawer = useCallback(
    async (cluster: FaceClusterSummary) => {
      setSelectedCluster(cluster);
      setClusterDrawerVisible(true);
      await fetchClusterFaces(cluster.cluster_id);
    },
    [fetchClusterFaces],
  );

  const closeClusterDrawer = useCallback(() => {
    setClusterDrawerVisible(false);
    setPhotoDetailDrawerVisible(false);
    setSelectedCluster(null);
    setSelectedClusterFace(null);
    setClusterFaces([]);
  }, []);

  const openClusterAssignModal = useCallback((cluster: FaceClusterSummary) => {
    setClusterAssignTarget(cluster);
    setClusterAssignPersonId(cluster.person_id || null);
    setClusterAssignVisible(true);
  }, []);

  const closeClusterAssignModal = useCallback(() => {
    setClusterAssignVisible(false);
    setClusterAssignTarget(null);
    setClusterAssignPersonId(null);
  }, []);

  const parseMetadataField = (value?: string) => {
    if (!value || !value.trim()) {
      return {};
    }
    try {
      return JSON.parse(value);
    } catch (err) {
      throw new Error('メタデータは有効なJSON形式で入力してください');
    }
  };

  const linkFacePhotoToPerson = useCallback(
    async (personId: string, facePhotoId: string) => {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERSONS}/${personId}/face-photos`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ face_photo_id: facePhotoId }),
        },
      );
      if (!response.ok) {
        throw new Error('顔写真の紐づけに失敗しました');
      }
    },
    [getAuthToken],
  );

  const assignPersonToCluster = useCallback(
    async (clusterId: string, personId: string | null) => {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERSONS}/face-clusters/${clusterId}/assign-person`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ person_id: personId }),
        },
      );
      if (!response.ok) {
        throw new Error('クラスタの人物紐づけに失敗しました');
      }
      const data: FaceClusterAssignResponse = await response.json();
      return data;
    },
    [getAuthToken],
  );

  const reclusterAssignedCluster = useCallback(
    async (clusterId: string) => {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERSONS}/face-clusters/${clusterId}/recluster-unassigned`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        throw new Error(detail?.detail || 'クラスタ再計算に失敗しました');
      }
      const data: FaceClusterReclusterResponse = await response.json();
      return data;
    },
    [getAuthToken],
  );

  const mergeUnassignedClustersIntoAssigned = useCallback(
    async (clusterId: string, clusterIds: string[]) => {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERSONS}/face-clusters/${clusterId}/merge-unassigned`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ cluster_ids: clusterIds }),
        },
      );
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        throw new Error(detail?.detail || '候補クラスタの吸収に失敗しました');
      }
      const data: FaceClusterReclusterResponse = await response.json();
      return data;
    },
    [getAuthToken],
  );

  const applyReclusterResultToState = useCallback((updatedCluster: FaceClusterReclusterResponse) => {
    const mergedClusterIds = updatedCluster.merged_cluster_ids || [];
    const removedClusterIds = new Set<string>(mergedClusterIds);

    setUnassignedFaceClusters((prev) => prev.filter((item) => !removedClusterIds.has(item.cluster_id)));
    setUnassignedFaceClustersTotal((prev) => Math.max(0, prev - mergedClusterIds.length));
    setAssignedFaceClusters((prev) => {
      const existingIndex = prev.findIndex((item) => item.cluster_id === updatedCluster.cluster_id);
      if (existingIndex < 0) {
        return [updatedCluster, ...prev];
      }
      const next = [...prev];
      next[existingIndex] = {
        ...next[existingIndex],
        ...updatedCluster,
        thumbnail_url: updatedCluster.thumbnail_url || next[existingIndex].thumbnail_url,
      };
      return next;
    });
    setSelectedCluster((prev) =>
      prev && prev.cluster_id === updatedCluster.cluster_id
        ? {
            ...prev,
            ...updatedCluster,
            thumbnail_url: updatedCluster.thumbnail_url || prev.thumbnail_url,
          }
        : prev,
    );
    setSelectedClusterIds((prev) => prev.filter((clusterId) => !removedClusterIds.has(clusterId)));
  }, []);

  const handleClusterAssign = useCallback(async () => {
    if (!clusterAssignTarget) {
      return;
    }
    setClusterAssigning(true);
    try {
      const sourceClusterId = clusterAssignTarget.cluster_id;
      const updatedCluster = await assignPersonToCluster(sourceClusterId, clusterAssignPersonId || null);
      const wasAssigned = Boolean(clusterAssignTarget.person_id);
      const isAssigned = Boolean(updatedCluster.person_id);

      if (wasAssigned || !isAssigned) {
        message.success('クラスタの人物紐づけを更新しました');
        closeClusterAssignModal();
        await refreshFaceClusters();
        return;
      }

      const mergedClusterIds = updatedCluster.merged_cluster_ids || [];
      const removedClusterIds = new Set<string>([sourceClusterId, ...mergedClusterIds]);
      const assignedAlreadyExists = assignedFaceClusters.some(
        (cluster) => cluster.cluster_id === updatedCluster.cluster_id,
      );

      setUnassignedFaceClusters((prev) =>
        prev.filter((cluster) => !removedClusterIds.has(cluster.cluster_id)),
      );
      setUnassignedFaceClustersTotal((prev) => Math.max(0, prev - 1));
      setAssignedFaceClusters((prev) => {
        const filtered = prev.filter(
          (cluster) => !removedClusterIds.has(cluster.cluster_id) || cluster.cluster_id === updatedCluster.cluster_id,
        );
        const existingIndex = filtered.findIndex(
          (cluster) => cluster.cluster_id === updatedCluster.cluster_id,
        );
        if (existingIndex >= 0) {
          const next = [...filtered];
          next[existingIndex] = {
            ...next[existingIndex],
            ...updatedCluster,
            thumbnail_url: updatedCluster.thumbnail_url || next[existingIndex].thumbnail_url,
          };
          return next;
        }
        return [updatedCluster, ...filtered];
      });
      setAssignedFaceClustersTotal((prev) => prev + (assignedAlreadyExists ? 0 : 1));
      setSelectedClusterIds((prev) => prev.filter((clusterId) => !removedClusterIds.has(clusterId)));

      message.success('クラスタの人物紐づけを更新しました');
      closeClusterAssignModal();
      await refreshUnassignedClusters();
      void syncAssignedClustersInBackground();
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'クラスタの人物紐づけに失敗しました';
      message.error(msg);
    } finally {
      setClusterAssigning(false);
    }
  }, [
    assignPersonToCluster,
    clusterAssignPersonId,
    clusterAssignTarget,
    closeClusterAssignModal,
    assignedFaceClusters,
    refreshFaceClusters,
    refreshUnassignedClusters,
    syncAssignedClustersInBackground,
  ]);

  const handleClusterRecluster = useCallback(
    async (cluster: FaceClusterSummary) => {
      if (!cluster.person_id) {
        message.warning('人物に紐づいたクラスタのみ再計算できます');
        return;
      }

      setReclusterModalTarget(cluster);
      setReclusterModalResult(null);
      setReclusterCandidateSelection([]);
      setReclusterModalVisible(true);
      setClusterRecalculatingId(cluster.cluster_id);
      try {
        const updatedCluster = await reclusterAssignedCluster(cluster.cluster_id);
        applyReclusterResultToState(updatedCluster);
        setReclusterModalResult(updatedCluster);
        const mergedCount = updatedCluster.merged_count || 0;
        const iterations = updatedCluster.iterations || 0;
        if (mergedCount > 0) {
          message.success(`再計算が完了しました（${mergedCount}件吸収 / ${iterations}反復）`);
        } else {
          message.success(`再計算が完了しました（吸収なし / ${iterations}反復）`);
        }
        await refreshUnassignedClusters();
        void syncAssignedClustersInBackground();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'クラスタ再計算に失敗しました';
        message.error(msg);
        setReclusterModalVisible(false);
        setReclusterModalTarget(null);
      } finally {
        setClusterRecalculatingId(null);
      }
    },
    [applyReclusterResultToState, reclusterAssignedCluster, refreshUnassignedClusters, syncAssignedClustersInBackground],
  );

  const closeReclusterModal = useCallback(() => {
    if (clusterRecalculatingId || reclusterCandidateMerging) {
      return;
    }
    setReclusterModalVisible(false);
    setReclusterModalTarget(null);
    setReclusterModalResult(null);
    setReclusterCandidateSelection([]);
  }, [clusterRecalculatingId, reclusterCandidateMerging]);

  const toggleReclusterCandidate = useCallback((clusterId: string, checked: boolean) => {
    setReclusterCandidateSelection((prev) => {
      if (checked) {
        if (prev.includes(clusterId)) {
          return prev;
        }
        return [...prev, clusterId];
      }
      return prev.filter((id) => id !== clusterId);
    });
  }, []);

  const handleMergeSuggestedClusters = useCallback(async () => {
    if (!reclusterModalTarget || reclusterCandidateSelection.length === 0) {
      message.warning('吸収する候補クラスタを選択してください');
      return;
    }

    setReclusterCandidateMerging(true);
    try {
      const updatedCluster = await mergeUnassignedClustersIntoAssigned(
        reclusterModalTarget.cluster_id,
        reclusterCandidateSelection,
      );
      applyReclusterResultToState(updatedCluster);
      setReclusterModalResult(updatedCluster);
      setReclusterCandidateSelection([]);
      const mergedCount = updatedCluster.merged_count || 0;
      if (mergedCount > 0) {
        message.success(`候補クラスタを吸収しました（${mergedCount}件）`);
      } else {
        message.info('吸収対象はありませんでした');
      }
      await refreshUnassignedClusters();
      void syncAssignedClustersInBackground();
    } catch (err) {
      const msg = err instanceof Error ? err.message : '候補クラスタの吸収に失敗しました';
      message.error(msg);
    } finally {
      setReclusterCandidateMerging(false);
    }
  }, [
    applyReclusterResultToState,
    mergeUnassignedClustersIntoAssigned,
    reclusterCandidateSelection,
    reclusterModalTarget,
    refreshUnassignedClusters,
    syncAssignedClustersInBackground,
  ]);

  const toggleClusterSelection = useCallback((clusterId: string, checked: boolean) => {
    setSelectedClusterIds((prev) => {
      if (checked) {
        if (prev.includes(clusterId)) {
          return prev;
        }
        return [...prev, clusterId];
      }
      return prev.filter((id) => id !== clusterId);
    });
  }, []);

  const selectAllClusters = useCallback(() => {
    setSelectedClusterIds([
      ...assignedFaceClusters.map((cluster) => cluster.cluster_id),
      ...unassignedFaceClusters.map((cluster) => cluster.cluster_id),
    ]);
  }, [assignedFaceClusters, unassignedFaceClusters]);

  const clearSelectedClusters = useCallback(() => {
    setSelectedClusterIds([]);
  }, []);

  const handleBulkClusterAssign = useCallback(async () => {
    if (selectedClusterIds.length === 0) {
      message.warning('一括紐づけするクラスタを選択してください');
      return;
    }
    if (!bulkClusterAssignPersonId) {
      message.warning('紐づけ先の人物を選択してください');
      return;
    }

    setBulkClusterAssigning(true);
    try {
      let successCount = 0;
      let failedCount = 0;
      for (const clusterId of selectedClusterIds) {
        try {
          await assignPersonToCluster(clusterId, bulkClusterAssignPersonId);
          successCount += 1;
        } catch (err) {
          failedCount += 1;
          secureLogger.warn('Failed to assign cluster in bulk operation', {
            clusterId,
            personId: bulkClusterAssignPersonId,
            err,
          });
        }
      }

      if (successCount > 0) {
        await refreshFaceClusters();
      }
      if (failedCount === 0) {
        message.success(`${successCount}件のクラスタを一括紐づけしました`);
      } else if (successCount === 0) {
        message.error('クラスタの一括紐づけに失敗しました');
      } else {
        message.warning(`一括紐づけ完了: 成功${successCount}件 / 失敗${failedCount}件`);
      }
      setSelectedClusterIds([]);
    } finally {
      setBulkClusterAssigning(false);
    }
  }, [assignPersonToCluster, bulkClusterAssignPersonId, refreshFaceClusters, selectedClusterIds]);

  const removeFaceFromCluster = useCallback(
    async (clusterId: string, facePhotoId: string) => {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERSONS}/face-clusters/${clusterId}/faces/${facePhotoId}/remove`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) {
        throw new Error('クラスタからの除外に失敗しました');
      }
    },
    [getAuthToken],
  );

  const setClusterRepresentativeFace = useCallback(
    async (clusterId: string, facePhotoId: string) => {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERSONS}/face-clusters/${clusterId}/representative`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ face_photo_id: facePhotoId }),
        },
      );
      if (!response.ok) {
        throw new Error('代表写真の設定に失敗しました');
      }
      const data: FaceClusterSummary = await response.json();
      return data;
    },
    [getAuthToken],
  );

  const setPersonRepresentativeFace = useCallback(
    async (personId: string, facePhotoId: string) => {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERSONS}/${personId}/representative-face`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ face_photo_id: facePhotoId }),
        },
      );
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        throw new Error(detail?.detail || '人物代表写真の設定に失敗しました');
      }
      const data: PersonDetail = await response.json();
      return data;
    },
    [getAuthToken],
  );

  const handleSubmit = useCallback(async () => {
    setSaving(true);
    try {
      const values = await form.validateFields();
      const metadata = parseMetadataField(values.metadataText);
      const payload: Record<string, unknown> = {
        display_name: values.display_name,
        phone_number: values.phone_number?.trim() || null,
        email: values.email?.trim() || null,
        affiliation: values.affiliation?.trim() || null,
        notes: values.notes?.trim() || null,
        metadata,
      };

      const token = await getAuthToken();
      const url = editingPerson
        ? `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERSONS}/${editingPerson.id}`
        : `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERSONS}`;
      const method = editingPerson ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(editingPerson ? '人物の更新に失敗しました' : '人物の登録に失敗しました');
      }
      const data = (await response.json().catch(() => null)) as PersonDetail | null;
      if (!editingPerson && data?.id) {
        const tasks: Array<Promise<unknown>> = [];
        if (pendingFacePhotoId) {
          tasks.push(linkFacePhotoToPerson(data.id, pendingFacePhotoId));
        }
        if (pendingClusterId) {
          tasks.push(assignPersonToCluster(pendingClusterId, data.id));
        }
        if (tasks.length > 0) {
          await Promise.all(tasks);
          message.success('人物を追加して紐づけを更新しました');
        } else {
          message.success('人物を追加しました');
        }
      } else {
        message.success(editingPerson ? '人物を更新しました' : '人物を追加しました');
      }
      handleModalClose();
      await fetchPersons();
      await fetchUnlinkedFaces();
      await refreshFaceClusters();
    } catch (err: any) {
      if (err?.errorFields) {
        return;
      }
      const messageText = err instanceof Error ? err.message : '処理に失敗しました';
      message.error(messageText);
    } finally {
      setSaving(false);
    }
  }, [
    editingPerson,
    fetchPersons,
    fetchUnlinkedFaces,
    form,
    getAuthToken,
    handleModalClose,
    assignPersonToCluster,
    refreshFaceClusters,
    linkFacePhotoToPerson,
    pendingFacePhotoId,
    pendingClusterId,
  ]);

  const handleDelete = useCallback((person: PersonSummary) => {
    Modal.confirm({
      title: `${person.display_name}を削除しますか？`,
      okType: 'danger',
      content: 'この操作は取り消せません。',
      onOk: async () => {
        try {
          const token = await getAuthToken();
          const response = await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERSONS}/${person.id}`,
            {
              method: 'DELETE',
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );
          if (!response.ok) {
            throw new Error('人物の削除に失敗しました');
          }
          message.success('人物を削除しました');
          await fetchPersons();
        } catch (err) {
          const msg = err instanceof Error ? err.message : '人物の削除に失敗しました';
          message.error(msg);
          throw err;
        }
      },
    });
  }, [fetchPersons, getAuthToken]);

  const handleRemoveClusterFace = useCallback(
    async (facePhotoId: string) => {
      if (!selectedCluster) {
        return;
      }
      try {
        await removeFaceFromCluster(selectedCluster.cluster_id, facePhotoId);
        message.success('クラスタから除外しました');
        await fetchClusterFaces(selectedCluster.cluster_id);
        await refreshFaceClusters();
        await fetchUnlinkedFaces();
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'クラスタからの除外に失敗しました';
        message.error(msg);
      }
    },
    [fetchClusterFaces, fetchUnlinkedFaces, refreshFaceClusters, removeFaceFromCluster, selectedCluster],
  );

  const handleSetClusterRepresentativeFace = useCallback(
    async (facePhotoId: string) => {
      if (!selectedCluster) {
        return;
      }
      setClusterRepresentativeUpdatingFaceId(facePhotoId);
      try {
        const updated = await setClusterRepresentativeFace(selectedCluster.cluster_id, facePhotoId);
        setSelectedCluster((prev) =>
          prev && prev.cluster_id === updated.cluster_id ? { ...prev, ...updated } : prev,
        );
        message.success('代表写真を設定しました');
        await refreshFaceClusters();
      } catch (err) {
        const msg = err instanceof Error ? err.message : '代表写真の設定に失敗しました';
        message.error(msg);
      } finally {
        setClusterRepresentativeUpdatingFaceId(null);
      }
    },
    [refreshFaceClusters, selectedCluster, setClusterRepresentativeFace],
  );

  const handleSetPersonRepresentativeFace = useCallback(
    async (facePhotoId: string) => {
      if (!detailPerson) {
        return;
      }
      setPersonRepresentativeUpdatingFaceId(facePhotoId);
      try {
        const updated = await setPersonRepresentativeFace(detailPerson.id, facePhotoId);
        setDetailData(updated);
        setDetailPerson((prev) =>
          prev && prev.id === updated.id
            ? {
                ...prev,
                representative_face_photo_id: updated.representative_face_photo_id,
                representative_thumbnail_url: updated.representative_thumbnail_url,
                updated_at: updated.updated_at,
              }
            : prev,
        );
        message.success('人物代表写真を設定しました');
        await fetchPersons();
      } catch (err) {
        const msg = err instanceof Error ? err.message : '人物代表写真の設定に失敗しました';
        message.error(msg);
      } finally {
        setPersonRepresentativeUpdatingFaceId(null);
      }
    },
    [detailPerson, fetchPersons, setPersonRepresentativeFace],
  );

  const closePhotoDetailDrawer = useCallback(() => {
    setPhotoDetailDrawerVisible(false);
    setSelectedClusterFace(null);
  }, []);

  const handleOpenOriginalPhoto = useCallback((face: FaceClusterFace) => {
    setSelectedClusterFace(face);
    setPhotoDetailDrawerVisible(true);
  }, []);

  const isOwner = useCallback((person: PersonSummary) => {
    if (!currentUserId) {
      return true;
    }
    return person.owner_user_id === currentUserId;
  }, [currentUserId]);

  const formatDurationMs = (value?: number | null) => {
    if (!value || value <= 0) return '0秒';
    const totalSeconds = Math.floor(value / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes <= 0) {
      return `${seconds}秒`;
    }
    return `${minutes}分${seconds.toString().padStart(2, '0')}秒`;
  };

  const formatTimestampFromMs = (value: number) => {
    const totalSeconds = Math.floor(value / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const millis = Math.floor((value % 1000) / 100);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${millis}`;
  };

  const handleRemoveSegment = useCallback(
    async (segment: EnrollmentSegment) => {
      if (!detailPerson || (currentUserId && detailPerson.owner_user_id !== currentUserId)) {
        return;
      }
      try {
        const token = await getAuthToken();
        const response = await fetch(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERSONS}/${detailPerson.id}/speaker-enrollments/${segment.id}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (!response.ok) {
          throw new Error('候補の削除に失敗しました');
        }
        message.success('エンロール候補を削除しました');
        await refreshDetail();
      } catch (err) {
        const text = err instanceof Error ? err.message : '候補の削除に失敗しました';
        message.error(text);
      }
    },
    [currentUserId, detailPerson, getAuthToken, refreshDetail],
  );

  const handleCreateEnrollmentJob = useCallback(async () => {
    if (!detailPerson || (currentUserId && detailPerson.owner_user_id !== currentUserId)) {
      return;
    }
    try {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERSONS}/${detailPerson.id}/speaker-enrollment-jobs`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        },
      );
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        const msg = detail?.detail || 'エンロールジョブの登録に失敗しました';
        throw new Error(msg);
      }
      message.success('エンロールジョブを登録しました');
      await refreshDetail();
      await fetchPersons();
    } catch (err) {
      const text = err instanceof Error ? err.message : 'エンロールジョブの登録に失敗しました';
      message.error(text);
    }
  }, [currentUserId, detailPerson, fetchPersons, getAuthToken, refreshDetail]);

  const personNameMap = useMemo(() => {
    return new Map(persons.map((person) => [person.id, person.display_name]));
  }, [persons]);

  const faceClusters = useMemo(
    () => [...assignedFaceClusters, ...unassignedFaceClusters],
    [assignedFaceClusters, unassignedFaceClusters],
  );

  const faceClustersTotal = useMemo(
    () => assignedFaceClustersTotal + unassignedFaceClustersTotal,
    [assignedFaceClustersTotal, unassignedFaceClustersTotal],
  );

  const handleUnassignedClusterPageChange = useCallback(
    (page: number) => {
      void refreshUnassignedClusters(page);
    },
    [refreshUnassignedClusters],
  );

  const columns: ColumnsType<PersonSummary> = useMemo(() => [
    {
      title: '顔画像',
      key: 'face_image',
      width: 92,
      render: (_, record) => {
        const thumbnailUrl = record.representative_thumbnail_url;
        if (!thumbnailUrl) {
          return (
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 6,
                border: '1px solid #f0f0f0',
                background: '#fafafa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999',
                fontSize: 11,
              }}
            >
              画像なし
            </div>
          );
        }
        return (
          <Image
            src={thumbnailUrl}
            alt={record.display_name}
            preview={false}
            width={56}
            height={56}
            style={{ borderRadius: 6, objectFit: 'cover' }}
          />
        );
      },
    },
    {
      title: '表示名',
      dataIndex: 'display_name',
      key: 'display_name',
      render: (text: string, record) => (
        <div>
          <Text strong style={{ display: 'block', fontSize: 15, lineHeight: 1.25 }}>
            {text}
          </Text>
          {record.affiliation && (
            <Paragraph
              type="secondary"
              style={{ marginBottom: 0, marginTop: 2, fontSize: 12, lineHeight: 1.25 }}
            >
              {record.affiliation}
            </Paragraph>
          )}
        </div>
      ),
    },
    {
      title: '連絡先',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div>{record.email || '-'}</div>
          <div>{record.phone_number || '-'}</div>
        </div>
      ),
    },
    {
      title: '状態',
      key: 'status',
      render: (_, record) => (
        <Space size={[4, 4]} wrap>
          <Tag color={isOwner(record) ? 'blue' : 'default'}>
            {isOwner(record) ? '自分の人物' : '共有'}
          </Tag>
          {record.linked_user_id && <Tag color="green">ユーザー連携</Tag>}
          {record.speaker_profile_uri && <Tag color="purple">エンロール済み</Tag>}
        </Space>
      ),
    },
    {
      title: '更新日時',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (value: string) => formatDateTime(value),
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      render: (_, record) => {
        const owner = isOwner(record);
        return (
          <Space>
            <Button size="small" onClick={() => openEditModal(record)} disabled={!owner}>
              編集
            </Button>
            <Button size="small" danger onClick={() => handleDelete(record)} disabled={!owner}>
              削除
            </Button>
            <Button size="small" onClick={() => openDetailDrawer(record)}>
              詳細
            </Button>
          </Space>
        );
      },
    },
  ], [handleDelete, isOwner, openDetailDrawer, openEditModal]);

  return (
    <>
      <Card
        title="人物管理"
        style={{ marginBottom: 16 }}
        extra={
          <Space size="middle">
            <span>共有を表示</span>
            <Switch checked={includeShared} onChange={setIncludeShared} />
            <Button icon={<ReloadOutlined />} onClick={fetchPersons} loading={loading}>
              更新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              新規追加
            </Button>
          </Space>
        }
      >
        <Paragraph type="secondary">
          親しい人や話者認識で使いたい人物を管理します。連絡先や補足メモを登録しておくと、データ整理がスムーズになります。
        </Paragraph>
        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 16 }}
          />
        )}
        <Table<PersonSummary>
          rowKey="id"
          columns={columns}
          dataSource={persons}
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: false }}
        />
      </Card>

      <Card
        title={`顔クラスタ (${faceClustersTotal}件)`}
        style={{ marginBottom: 16 }}
        extra={
          <Space wrap>
            <Select
              value={bulkClusterAssignPersonId ?? undefined}
              allowClear
              placeholder="一括紐づけ先"
              style={{ width: 220 }}
              options={persons.map((person) => ({
                value: person.id,
                label: person.display_name,
              }))}
              onChange={(value) => setBulkClusterAssignPersonId(value ?? null)}
              disabled={faceClustersLoading || bulkClusterAssigning}
            />
            <Button
              type="primary"
              onClick={handleBulkClusterAssign}
              disabled={selectedClusterIds.length === 0}
              loading={bulkClusterAssigning}
            >
              一括紐づけ ({selectedClusterIds.length})
            </Button>
            <Button
              onClick={selectAllClusters}
              disabled={faceClustersLoading || faceClusters.length === 0 || bulkClusterAssigning}
            >
              全選択
            </Button>
            <Button onClick={clearSelectedClusters} disabled={selectedClusterIds.length === 0 || bulkClusterAssigning}>
              選択解除
            </Button>
            <Button icon={<ReloadOutlined />} onClick={refreshFaceClusters} loading={faceClustersLoading}>
              更新
            </Button>
          </Space>
        }
      >
        {faceClustersLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
            <Spin />
          </div>
        ) : faceClustersTotal === 0 ? (
          <Empty description="顔クラスタはまだありません" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <>
            <Card size="small" title={`紐づけ済み顔クラスタ (${assignedFaceClustersTotal}件)`} style={{ marginBottom: 12 }}>
              {assignedFaceClusters.length === 0 ? (
                <Empty description="紐づけ済み顔クラスタはありません" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
                  {assignedFaceClusters.map((cluster) => {
                    const assignedName = cluster.person_id
                      ? personNameMap.get(cluster.person_id) || '人物未取得'
                      : null;
                    const isSelected = selectedClusterIds.includes(cluster.cluster_id);
                    return (
                      <Card
                        key={cluster.cluster_id}
                        size="small"
                        bodyStyle={{ padding: 8 }}
                        style={isSelected ? { borderColor: '#1677ff' } : undefined}
                      >
                        <div style={{ marginBottom: 6 }}>
                          <Checkbox
                            checked={isSelected}
                            onChange={(event) =>
                              toggleClusterSelection(cluster.cluster_id, event.target.checked)
                            }
                            disabled={bulkClusterAssigning}
                          >
                            選択
                          </Checkbox>
                        </div>
                        {cluster.thumbnail_url ? (
                          <Image
                            src={cluster.thumbnail_url}
                            alt={cluster.cluster_id}
                            preview={false}
                            width="100%"
                            style={{ borderRadius: 6, cursor: 'pointer' }}
                            onClick={() => void openClusterDrawer(cluster)}
                          />
                        ) : (
                          <div
                            style={{
                              width: '100%',
                              height: 120,
                              background: '#f5f5f5',
                              borderRadius: 6,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#999',
                              fontSize: 12,
                            }}
                          >
                            画像なし
                          </div>
                        )}
                        <Space direction="vertical" size={4} style={{ marginTop: 8 }}>
                          <Space size={6} wrap>
                            <Tag color="blue">{cluster.size}枚</Tag>
                            {assignedName ? (
                              <Tag color="green">{assignedName}</Tag>
                            ) : (
                              <Tag color="default">未登録</Tag>
                            )}
                          </Space>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {cluster.event_key}
                          </Text>
                          {cluster.updated_at && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              更新: {formatDateTime(cluster.updated_at)}
                            </Text>
                          )}
                        </Space>
                        <Space size={6} style={{ marginTop: 8 }}>
                          <Button
                            size="small"
                            icon={<ReloadOutlined />}
                            loading={clusterRecalculatingId === cluster.cluster_id}
                            onClick={() => void handleClusterRecluster(cluster)}
                          >
                            再計算
                          </Button>
                          <Button size="small" onClick={() => openClusterAssignModal(cluster)}>
                            紐づけ
                          </Button>
                        </Space>
                      </Card>
                    );
                  })}
                </div>
              )}
            </Card>
            <Card size="small" title={`未紐づけ顔クラスタ (${unassignedFaceClustersTotal}件)`}>
              {unassignedFaceClustersTotal === 0 ? (
                <Empty description="未紐づけ顔クラスタはありません" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <>
                  <div
                    style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}
                  >
                    {unassignedFaceClusters.map((cluster) => {
                      const assignedName = cluster.person_id
                        ? personNameMap.get(cluster.person_id) || '人物未取得'
                        : null;
                      const isSelected = selectedClusterIds.includes(cluster.cluster_id);
                      return (
                        <Card
                          key={cluster.cluster_id}
                          size="small"
                          bodyStyle={{ padding: 8 }}
                          style={isSelected ? { borderColor: '#1677ff' } : undefined}
                        >
                          <div style={{ marginBottom: 6 }}>
                            <Checkbox
                              checked={isSelected}
                              onChange={(event) =>
                                toggleClusterSelection(cluster.cluster_id, event.target.checked)
                              }
                              disabled={bulkClusterAssigning}
                            >
                              選択
                            </Checkbox>
                          </div>
                          {cluster.thumbnail_url ? (
                            <Image
                              src={cluster.thumbnail_url}
                              alt={cluster.cluster_id}
                              preview={false}
                              width="100%"
                              style={{ borderRadius: 6, cursor: 'pointer' }}
                              onClick={() => void openClusterDrawer(cluster)}
                            />
                          ) : (
                            <div
                              style={{
                                width: '100%',
                                height: 120,
                                background: '#f5f5f5',
                                borderRadius: 6,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#999',
                                fontSize: 12,
                              }}
                            >
                              画像なし
                            </div>
                          )}
                          <Space direction="vertical" size={4} style={{ marginTop: 8 }}>
                            <Space size={6} wrap>
                              <Tag color="blue">{cluster.size}枚</Tag>
                              {assignedName ? (
                                <Tag color="green">{assignedName}</Tag>
                              ) : (
                                <Tag color="default">未登録</Tag>
                              )}
                            </Space>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {cluster.event_key}
                            </Text>
                            {cluster.updated_at && (
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                更新: {formatDateTime(cluster.updated_at)}
                              </Text>
                            )}
                          </Space>
                          <Space size={6} style={{ marginTop: 8 }}>
                            <Button
                              size="small"
                              type="primary"
                              onClick={() => openCreateModalFromCluster(cluster.cluster_id)}
                            >
                              人物登録
                            </Button>
                            <Button size="small" onClick={() => openClusterAssignModal(cluster)}>
                              紐づけ
                            </Button>
                          </Space>
                        </Card>
                      );
                    })}
                  </div>
                  {unassignedFaceClustersTotal > UNASSIGNED_CLUSTER_PAGE_SIZE && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                      <Pagination
                        current={unassignedClusterPage}
                        pageSize={UNASSIGNED_CLUSTER_PAGE_SIZE}
                        total={unassignedFaceClustersTotal}
                        showSizeChanger={false}
                        onChange={handleUnassignedClusterPageChange}
                        disabled={faceClustersLoading || unassignedClustersRefreshing}
                      />
                    </div>
                  )}
                </>
              )}
            </Card>
          </>
        )}
      </Card>

      <Modal
        title={editingPerson ? '人物を編集' : '人物を追加'}
        open={modalVisible}
        onCancel={handleModalClose}
        onOk={handleSubmit}
        confirmLoading={saving}
        okText={editingPerson ? '更新' : '作成'}
        cancelText="キャンセル"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="表示名"
            name="display_name"
            rules={[{ required: true, message: '表示名を入力してください' }]}
          >
            <Input placeholder="山田 太郎" />
          </Form.Item>
          <Form.Item label="メールアドレス" name="email">
            <Input placeholder="example@example.com" />
          </Form.Item>
          <Form.Item label="電話番号" name="phone_number">
            <Input placeholder="090-xxxx-xxxx" />
          </Form.Item>
          <Form.Item label="所属" name="affiliation" rules={[{ max: 32, message: '所属は32文字以内で入力してください' }]}>
            <Input maxLength={32} placeholder="所属を入力（任意）" />
          </Form.Item>
          <Form.Item label="メモ" name="notes">
            <TextArea rows={3} placeholder="備考や特徴など" />
          </Form.Item>
          <Form.Item label="メタデータ(JSON)" name="metadataText">
            <TextArea rows={4} placeholder='{"company":"Thinklet"} など任意のJSON' />
          </Form.Item>
          {pendingFacePhotoId && !editingPerson && (
            <Alert
              type="info"
              showIcon
              message="未登録の顔写真を紐づけて登録します"
              style={{ marginTop: 8 }}
            />
          )}
        </Form>
      </Modal>

      <Modal
        title="クラスタを人物に紐づけ"
        open={clusterAssignVisible}
        onCancel={closeClusterAssignModal}
        onOk={handleClusterAssign}
        confirmLoading={clusterAssigning}
        okText="更新"
        cancelText="キャンセル"
      >
        <Form layout="vertical">
          <Form.Item label="人物を選択（空にすると解除）">
            <Select
              value={clusterAssignPersonId ?? undefined}
              allowClear
              placeholder="人物を選択"
              options={persons.map((person) => ({
                value: person.id,
                label: person.display_name,
              }))}
              onChange={(value) => setClusterAssignPersonId(value ?? null)}
            />
          </Form.Item>
          {clusterAssignTarget && (
            <Text type="secondary">
              対象クラスタ: {clusterAssignTarget.cluster_id}
            </Text>
          )}
        </Form>
      </Modal>

      <Modal
        title="クラスタ再計算"
        open={reclusterModalVisible}
        onCancel={closeReclusterModal}
        footer={[
          <Button key="close" onClick={closeReclusterModal} disabled={Boolean(clusterRecalculatingId) || reclusterCandidateMerging}>
            閉じる
          </Button>,
          <Button
            key="merge"
            type="primary"
            onClick={() => void handleMergeSuggestedClusters()}
            loading={reclusterCandidateMerging}
            disabled={
              Boolean(clusterRecalculatingId) ||
              !reclusterModalResult ||
              reclusterCandidateSelection.length === 0
            }
          >
            選択候補を吸収 ({reclusterCandidateSelection.length})
          </Button>,
        ]}
        width={860}
        destroyOnClose
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {reclusterModalTarget && (
            <Alert
              type="info"
              showIcon
              message={`対象クラスタ: ${reclusterModalTarget.cluster_id}`}
              description="まず自動再計算で閾値以内の未紐づけクラスタを吸収し、その後に近傍候補を手動で確認できます。"
            />
          )}

          {clusterRecalculatingId ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
              <Space direction="vertical" size={12} align="center">
                <Spin size="large" />
                <Text>自動再計算を実行中...</Text>
              </Space>
            </div>
          ) : reclusterModalResult ? (
            <>
              <Alert
                type="success"
                showIcon
                message={`自動再計算完了: ${reclusterModalResult.merged_count || 0}件吸収 / ${reclusterModalResult.iterations || 0}反復`}
                description={
                  reclusterModalResult.auto_threshold != null && reclusterModalResult.candidate_threshold != null
                    ? `自動吸収閾値: ${reclusterModalResult.auto_threshold.toFixed(3)} / 手動候補上限: ${reclusterModalResult.candidate_threshold.toFixed(3)}`
                    : undefined
                }
              />
              {(reclusterModalResult.suggested_clusters || []).length === 0 ? (
                <Empty
                  description="手動確認が必要な近傍候補はありません"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <>
                  <Text strong>
                    近傍候補クラスタ
                    <Text type="secondary">（閾値外だが近い未紐づけクラスタ）</Text>
                  </Text>
                  <div
                    style={{
                      display: 'grid',
                      gap: 12,
                      gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                    }}
                  >
                    {(reclusterModalResult.suggested_clusters || []).map((candidate: FaceClusterReclusterCandidate) => {
                      const checked = reclusterCandidateSelection.includes(candidate.cluster_id);
                      return (
                        <Card
                          key={candidate.cluster_id}
                          size="small"
                          bodyStyle={{ padding: 8 }}
                          style={checked ? { borderColor: '#1677ff' } : undefined}
                        >
                          <div style={{ marginBottom: 6 }}>
                            <Checkbox
                              checked={checked}
                              onChange={(event) => toggleReclusterCandidate(candidate.cluster_id, event.target.checked)}
                              disabled={reclusterCandidateMerging}
                            >
                              候補に追加
                            </Checkbox>
                          </div>
                          {candidate.thumbnail_url ? (
                            <Image
                              src={candidate.thumbnail_url}
                              alt={candidate.cluster_id}
                              preview={false}
                              width="100%"
                              style={{ borderRadius: 6 }}
                            />
                          ) : (
                            <div
                              style={{
                                width: '100%',
                                height: 120,
                                background: '#f5f5f5',
                                borderRadius: 6,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#999',
                                fontSize: 12,
                              }}
                            >
                              画像なし
                            </div>
                          )}
                          <Space direction="vertical" size={4} style={{ marginTop: 8 }}>
                            <Space size={6} wrap>
                              <Tag color="blue">{candidate.size}枚</Tag>
                              <Tag color="gold">距離 {candidate.distance.toFixed(3)}</Tag>
                            </Space>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {candidate.event_key}
                            </Text>
                            {candidate.updated_at && (
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                更新: {formatDateTime(candidate.updated_at)}
                              </Text>
                            )}
                          </Space>
                        </Card>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          ) : null}
        </Space>
      </Modal>

      <Drawer
        title="クラスタ詳細"
        open={clusterDrawerVisible}
        onClose={closeClusterDrawer}
        width={720}
        destroyOnClose
      >
        {!selectedCluster ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
            <Spin />
          </div>
        ) : (
          <>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="クラスタID">
                {selectedCluster.cluster_id}
              </Descriptions.Item>
              <Descriptions.Item label="イベントキー">
                {selectedCluster.event_key}
              </Descriptions.Item>
              <Descriptions.Item label="枚数">
                {selectedCluster.size}
              </Descriptions.Item>
              <Descriptions.Item label="人物">
                {selectedCluster.person_id
                  ? personNameMap.get(selectedCluster.person_id) || selectedCluster.person_id
                  : '未登録'}
              </Descriptions.Item>
            </Descriptions>

            <Divider>クラスタ内の顔</Divider>
            {clusterFacesLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                <Spin />
              </div>
            ) : clusterFaces.length === 0 ? (
              <Empty description="クラスタ内の顔がありません" />
            ) : (
              <div
                style={{
                  display: 'grid',
                  gap: 12,
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                }}
              >
                {clusterFaces.map((face) => (
                  <Card
                    key={face.face_photo_id}
                    size="small"
                    bodyStyle={{ padding: 8, textAlign: 'center' }}
                  >
                    {face.thumbnail_url ? (
                      <Image
                        src={face.thumbnail_url}
                        alt={face.face_photo_id}
                        preview={false}
                        width="100%"
                        style={{ borderRadius: 6, cursor: 'pointer' }}
                        onClick={() => handleOpenOriginalPhoto(face)}
                      />
                    ) : (
                      <div
                        style={{
                          width: '100%',
                          height: 100,
                          background: '#f5f5f5',
                          borderRadius: 6,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#999',
                          fontSize: 12,
                        }}
                      >
                        画像なし
                      </div>
                    )}
                    <Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
                      顔領域信頼度: {face.detection_score?.toFixed(2) ?? '-'}
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginTop: 2, fontSize: 12 }}>
                      代表スコア: {face.representative_score?.toFixed(2) ?? '-'}
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginTop: 2, fontSize: 12 }}>
                      同一人物信頼度: {face.identity_confidence?.toFixed(2) ?? '-'}
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginTop: 2, fontSize: 12 }}>
                      中心距離: {face.centroid_distance?.toFixed(3) ?? '-'}
                    </Text>
                    <Space size={6} style={{ marginTop: 6 }}>
                      <Button
                        size="small"
                        type={selectedCluster.representative_face_photo_id === face.face_photo_id ? 'default' : 'primary'}
                        onClick={() => handleSetClusterRepresentativeFace(face.face_photo_id)}
                        loading={clusterRepresentativeUpdatingFaceId === face.face_photo_id}
                        disabled={selectedCluster.representative_face_photo_id === face.face_photo_id}
                      >
                        {selectedCluster.representative_face_photo_id === face.face_photo_id ? '代表写真' : '代表に設定'}
                      </Button>
                    </Space>
                    <Popconfirm
                      title="クラスタから除外しますか？"
                      okText="除外"
                      cancelText="キャンセル"
                      onConfirm={() => handleRemoveClusterFace(face.face_photo_id)}
                    >
                      <Button size="small" danger style={{ marginTop: 6 }}>
                        除外
                      </Button>
                    </Popconfirm>
                  </Card>
                ))}
              </div>
            )}

            <Drawer
              title="元写真の詳細"
              open={photoDetailDrawerVisible}
              onClose={closePhotoDetailDrawer}
              placement="right"
              width={720}
              destroyOnClose
            >
              {!selectedClusterFace ? (
                <Empty description="写真情報がありません" />
              ) : (
                <>
                  <div
                    style={{
                      textAlign: 'center',
                      marginBottom: 16,
                    }}
                  >
                    {selectedClusterFace.original_photo_url || selectedClusterFace.thumbnail_url ? (
                      <Image
                        src={selectedClusterFace.original_photo_url || selectedClusterFace.thumbnail_url || undefined}
                        alt={selectedClusterFace.photo_id}
                        style={{ maxWidth: '100%', maxHeight: 360, objectFit: 'contain' }}
                      />
                    ) : (
                      <div
                        style={{
                          height: 220,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#999',
                          background: '#f5f5f5',
                          borderRadius: 8,
                          border: '1px solid #e0e0e0',
                        }}
                      >
                        画像を取得できません
                      </div>
                    )}
                  </div>

                  <Descriptions title="写真情報" bordered column={2} size="small">
                    <Descriptions.Item label="写真ID" span={2}>
                      {selectedClusterFace.photo_id}
                    </Descriptions.Item>
                    <Descriptions.Item label="撮影日時">
                      {formatDateTime(selectedClusterFace.captured_at)}
                    </Descriptions.Item>
                    <Descriptions.Item label="受信日時">
                      {formatDateTime(selectedClusterFace.received_at)}
                    </Descriptions.Item>
                    <Descriptions.Item label="ファイルサイズ">
                      {typeof selectedClusterFace.file_size === 'number'
                        ? `${(selectedClusterFace.file_size / 1024 / 1024).toFixed(2)} MB`
                        : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="解像度">
                      {typeof selectedClusterFace.image_width === 'number' &&
                      typeof selectedClusterFace.image_height === 'number'
                        ? `${selectedClusterFace.image_width} x ${selectedClusterFace.image_height}`
                        : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="カメラ">
                      {[selectedClusterFace.camera_make, selectedClusterFace.camera_model]
                        .filter(Boolean)
                        .join(' ') || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="ISO">
                      {selectedClusterFace.iso_speed ?? '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="絞り">
                      {typeof selectedClusterFace.aperture_value === 'number'
                        ? `F/${selectedClusterFace.aperture_value}`
                        : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="シャッター速度">
                      {typeof selectedClusterFace.exposure_time === 'number' && selectedClusterFace.exposure_time > 0
                        ? `1/${Math.round(1 / selectedClusterFace.exposure_time)}秒`
                        : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="焦点距離">
                      {typeof selectedClusterFace.focal_length === 'number'
                        ? `${selectedClusterFace.focal_length}mm`
                        : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="GPS座標" span={2}>
                      {typeof selectedClusterFace.gps_latitude === 'number' &&
                      typeof selectedClusterFace.gps_longitude === 'number' ? (
                        <>
                          <EnvironmentOutlined /> {selectedClusterFace.gps_latitude.toFixed(6)},{' '}
                          {selectedClusterFace.gps_longitude.toFixed(6)}
                          {typeof selectedClusterFace.gps_altitude === 'number'
                            ? ` (高度: ${selectedClusterFace.gps_altitude.toFixed(1)}m)`
                            : ''}
                        </>
                      ) : (
                        '-'
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="IMU情報" span={2}>
                      {typeof selectedClusterFace.imu_pitch === 'number' ||
                      typeof selectedClusterFace.imu_roll === 'number' ||
                      typeof selectedClusterFace.imu_orientation === 'number'
                        ? `${typeof selectedClusterFace.imu_pitch === 'number' ? `ピッチ: ${selectedClusterFace.imu_pitch.toFixed(1)}° ` : ''}${
                            typeof selectedClusterFace.imu_roll === 'number' ? `ロール: ${selectedClusterFace.imu_roll.toFixed(1)}° ` : ''
                          }${
                            typeof selectedClusterFace.imu_orientation === 'number'
                              ? `方位: ${selectedClusterFace.imu_orientation.toFixed(1)}°`
                              : ''
                          }`
                        : '-'}
                    </Descriptions.Item>
                  </Descriptions>

                  {typeof selectedClusterFace.gps_latitude === 'number' &&
                    typeof selectedClusterFace.gps_longitude === 'number' && (
                      <div style={{ marginTop: 16 }}>
                        <PhotoLocationMap
                          latitude={selectedClusterFace.gps_latitude}
                          longitude={selectedClusterFace.gps_longitude}
                          heading={
                            typeof selectedClusterFace.imu_orientation === 'number'
                              ? selectedClusterFace.imu_orientation
                              : null
                          }
                        />
                      </div>
                    )}
                </>
              )}
            </Drawer>

          </>
        )}
      </Drawer>

      <Drawer
        title={detailPerson ? `${detailPerson.display_name} の詳細` : '人物詳細'}
        open={detailVisible}
        onClose={closeDetailDrawer}
        width={840}
        destroyOnClose
      >
        {detailLoading || !detailData ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
            <Spin />
          </div>
        ) : (
          <>
            {detailPerson && (
              <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                {canEditDetail
                  ? 'この人物はあなたが管理しています。'
                  : '共有された人物のため、エンロール操作は行えません。'}
              </Paragraph>
            )}
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="表示名">
                <Space>
                  <Text strong>{detailData.display_name}</Text>
                  {detailData.speaker_profile_uri && <Tag color="purple">エンロール済み</Tag>}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="顔写真">
                {detailData.representative_thumbnail_url ? (
                  <Space size={12} align="start">
                    <Image
                      src={detailData.representative_thumbnail_url}
                      alt={detailData.display_name}
                      preview={false}
                      width={72}
                      height={72}
                      style={{ borderRadius: 6, objectFit: 'cover' }}
                    />
                    <Space direction="vertical" size={2}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        人物代表写真
                      </Text>
                      {detailData.representative_face_photo_id && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {detailData.representative_face_photo_id}
                        </Text>
                      )}
                    </Space>
                  </Space>
                ) : (
                  <Text type="secondary">代表写真未設定</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="連絡先">
                <div>{detailData.email || '-'}</div>
                <div>{detailData.phone_number || '-'}</div>
              </Descriptions.Item>
              <Descriptions.Item label="所属">
                {detailData.affiliation || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="備考">
                {detailData.notes || '-'}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Card
              size="small"
              title={`人物の顔写真 (${detailData.face_photos.length}件)`}
              style={{ marginBottom: 16 }}
            >
              {detailData.face_photos.length === 0 ? (
                <Empty description="紐づけ済みの顔写真がありません" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: 12,
                  }}
                >
                  {detailData.face_photos.map((face) => {
                    const isRepresentative =
                      detailData.representative_face_photo_id === face.face_photo_id;
                    return (
                      <Card
                        key={face.face_photo_id}
                        size="small"
                        bodyStyle={{ padding: 8, textAlign: 'center' }}
                      >
                        {face.thumbnail_url ? (
                          <Image
                            src={face.thumbnail_url}
                            alt={face.face_photo_id}
                            preview={false}
                            width="100%"
                            height={96}
                            style={{ borderRadius: 6, objectFit: 'cover' }}
                          />
                        ) : (
                          <div
                            style={{
                              width: '100%',
                              height: 96,
                              borderRadius: 6,
                              border: '1px solid #f0f0f0',
                              background: '#fafafa',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#999',
                              fontSize: 12,
                            }}
                          >
                            画像なし
                          </div>
                        )}
                        <Button
                          size="small"
                          type={isRepresentative ? 'default' : 'primary'}
                          style={{ marginTop: 8 }}
                          disabled={!canEditDetail || isRepresentative}
                          loading={personRepresentativeUpdatingFaceId === face.face_photo_id}
                          onClick={() => handleSetPersonRepresentativeFace(face.face_photo_id)}
                        >
                          {isRepresentative ? '人物代表' : '人物代表に設定'}
                        </Button>
                      </Card>
                    );
                  })}
                </div>
              )}
            </Card>

            <Divider />

            <Card size="small" title="エンロール進捗" style={{ marginBottom: 16 }}>
              {detailData.enrollment_summary ? (
                <>
                  <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                    <div>
                      <Text>
                        収集済み: {formatDurationMs(detailData.enrollment_summary.total_duration_ms)} /{' '}
                        {formatDurationMs(detailData.enrollment_summary.required_duration_ms)}
                      </Text>
                      <Progress
                        percent={Math.min(
                          100,
                          Math.round(
                            (detailData.enrollment_summary.total_duration_ms /
                              detailData.enrollment_summary.required_duration_ms) *
                              100,
                          ),
                        )}
                        showInfo={false}
                        size="small"
                      />
                      <Text type="secondary">
                        残り: {formatDurationMs(detailData.enrollment_summary.remaining_duration_ms)}
                      </Text>
                    </div>
                    <Tooltip
                      title={
                        detailData.enrollment_summary.ready
                          ? 'エンロールを実行できます'
                          : '120秒以上収集するとエンロール可能になります'
                      }
                    >
                      <div>
                        <Button
                          type="primary"
                          disabled={!detailData.enrollment_summary.ready || !canEditDetail}
                          onClick={handleCreateEnrollmentJob}
                        >
                          エンロールジョブを作成
                        </Button>
                        {!canEditDetail && (
                          <Paragraph type="secondary" style={{ marginTop: 8 }}>
                            共有された人物は操作できません。
                          </Paragraph>
                        )}
                      </div>
                    </Tooltip>
                  </Space>
                </>
              ) : (
                <Text type="secondary">まだエンロール候補がありません</Text>
              )}
            </Card>

            <Divider />

            <Card
              size="small"
              title={`エンロール候補 (${detailData.speaker_enrollments.length}件)`}
              style={{ marginBottom: 16 }}
            >
              {detailData.speaker_enrollments.length === 0 ? (
                <Empty description="エンロール候補がありません" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <Table<EnrollmentSegment>
                  rowKey="id"
                  dataSource={detailData.speaker_enrollments}
                  pagination={{ pageSize: 5 }}
                  columns={[
                    {
                      title: '音声',
                      dataIndex: 'audio_id',
                      render: (_, record) => (
                        <Space direction="vertical" size={4}>
                          <Link to={`/audio/${record.audio_id}`}>詳細を開く</Link>
                          {record.device_id ? (
                            <SegmentAudioPlayer
                              audioId={record.audio_id}
                              deviceId={record.device_id}
                              startSec={record.start_ms / 1000}
                              endSec={record.end_ms / 1000}
                              inline
                              label="プレビュー"
                            />
                          ) : (
                            <Text type="secondary">デバイス情報なし</Text>
                          )}
                        </Space>
                      ),
                    },
                    {
                      title: '区間',
                      key: 'range',
                      render: (_, record) =>
                        `${formatTimestampFromMs(record.start_ms)} - ${formatTimestampFromMs(record.end_ms)}`,
                    },
                    {
                      title: '長さ',
                      dataIndex: 'duration_ms',
                      render: (value: number) => formatDurationMs(value),
                    },
                    {
                      title: '追加日時',
                      dataIndex: 'created_at',
                      render: (value?: string | null) => formatDateTime(value),
                    },
                    {
                      title: '操作',
                      key: 'actions',
                      render: (_, record) => (
                        <Popconfirm
                          title="この候補を削除しますか？"
                          onConfirm={() => handleRemoveSegment(record)}
                          okText="削除"
                          cancelText="キャンセル"
                          disabled={!canEditDetail}
                        >
                          <Button danger size="small" disabled={!canEditDetail}>
                            削除
                          </Button>
                        </Popconfirm>
                      ),
                    },
                  ]}
                />
              )}
            </Card>

            <Card size="small" title="エンロールジョブ履歴">
              {detailData.speaker_enrollment_jobs.length === 0 ? (
                <Empty description="まだジョブ履歴がありません" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <Table<EnrollmentJob>
                  rowKey="id"
                  dataSource={detailData.speaker_enrollment_jobs}
                  size="small"
                  pagination={{ pageSize: 5 }}
                  columns={[
                    {
                      title: 'ジョブID',
                      dataIndex: 'id',
                      render: (value: string) => value.slice(0, 8),
                    },
                    {
                      title: '状態',
                      dataIndex: 'status',
                      render: (status: string) => {
                        const colorMap: Record<string, string> = {
                          pending: 'gold',
                          running: 'blue',
                          succeeded: 'green',
                          failed: 'red',
                        };
                        return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
                      },
                    },
                    {
                      title: '合計長さ',
                      dataIndex: 'total_duration_ms',
                      render: (value: number) => formatDurationMs(value),
                    },
                    {
                      title: '登録時刻',
                      dataIndex: 'queued_at',
                      render: (value?: string | null) => formatDateTime(value),
                    },
                    {
                      title: '完了時刻',
                      dataIndex: 'completed_at',
                      render: (value?: string | null) => formatDateTime(value),
                    },
                  ]}
                />
              )}
            </Card>
          </>
        )}
      </Drawer>
    </>
  );
};

export default PersonsPage;
